import { z } from 'zod';
import type { PartnerInboxVolume, PartnerUseCase } from '@trulyinbox/types';
import { apiFetch } from './api';
import { availableSlotsSchema, type AvailableSlot } from '@/features/onboarding/schemas';

/**
 * Request wrappers + Zod schemas for the internal (JWT-guarded)
 * `/partner/integration` surface — a partner's registered callback host and the
 * bearer token TrulyInbox sends them on every callback.
 *
 * All four routes are org-`OWNER` only server-side; the UI still renders for
 * non-owners and lets the 403 surface, matching how the rest of Settings
 * behaves rather than hiding the section and leaving a partner unsure it exists.
 *
 * **There is no field for reading the token back.** The server never returns it,
 * not even masked — a partner who has lost it rotates rather than reads.
 */

// ── Schemas ──────────────────────────────────────────────────────────────────

/** Mirrors `PartnerIntegrationStatus` in `@trulyinbox/database` (a plain varchar column). */
export const partnerIntegrationStatusSchema = z.enum(['unverified', 'active', 'disabled']);

export const partnerIntegrationSchema = z.object({
  id: z.number(),
  host: z.string(),
  status: partnerIntegrationStatusSchema,
  lastVerifiedAt: z.string().nullable(),
  /** Mailboxes linked to this integration, in any status — what Remove deletes. */
  linkedAccounts: z.number(),
});

export const removePartnerIntegrationResultSchema = z.object({
  deletedAccounts: z.number(),
});

export const partnerRemovalCodeResultSchema = z.object({
  /** Owners the code was actually emailed to. */
  sentTo: z.number(),
  expiresInSeconds: z.number(),
  resendAfterSeconds: z.number(),
});

// ── Types ────────────────────────────────────────────────────────────────────

export type PartnerIntegrationDto = z.infer<typeof partnerIntegrationSchema>;
export type PartnerIntegrationStatus = z.infer<typeof partnerIntegrationStatusSchema>;
export type RemovePartnerIntegrationResult = z.infer<typeof removePartnerIntegrationResultSchema>;
export type PartnerRemovalCodeResult = z.infer<typeof partnerRemovalCodeResultSchema>;

export type RegisterPartnerIntegrationInput = {
  /** HTTPS origin, no path. The server re-validates and normalizes. */
  host: string;
  /** ≥ 32 chars. Write-only — never returned by any route. */
  accessToken: string;
};

/** Matches the service-side minimum, so the form can say so before submitting. */
export const MIN_CALLBACK_TOKEN_LENGTH = 32;

// ── Requests ─────────────────────────────────────────────────────────────────

/**
 * `null` when nothing is registered.
 *
 * The endpoint 404s in that case, which is correct for an API but wrong for a
 * settings panel: "you have not set this up" is an ordinary state, not an error,
 * and letting the 404 reach the query would render an error banner on a fresh
 * organization. Every OTHER failure still throws.
 */
export async function getPartnerIntegration(): Promise<PartnerIntegrationDto | null> {
  try {
    const data = await apiFetch<unknown>('/partner/integration');
    return partnerIntegrationSchema.parse(data);
  } catch (err) {
    if (isNotRegistered(err)) return null;
    throw err;
  }
}

/** Register or rotate. The server verifies the pair against the live endpoint first. */
export async function registerPartnerIntegration(
  input: RegisterPartnerIntegrationInput,
): Promise<PartnerIntegrationDto> {
  const data = await apiFetch<unknown>('/partner/integration', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return partnerIntegrationSchema.parse(data);
}

/** Re-run the verification ping against the STORED pair. Does not rotate. */
export async function verifyPartnerIntegration(): Promise<PartnerIntegrationDto> {
  const data = await apiFetch<unknown>('/partner/integration/verify', { method: 'POST' });
  return partnerIntegrationSchema.parse(data);
}

/**
 * Email a 6-digit removal confirmation code to every owner of the org. Valid
 * for 10 minutes; requesting another replaces the pending one.
 */
export async function requestPartnerRemovalCode(): Promise<PartnerRemovalCodeResult> {
  const data = await apiFetch<unknown>('/partner/integration/removal-code', { method: 'POST' });
  return partnerRemovalCodeResultSchema.parse(data);
}

/**
 * Removal deletes every mailbox linked to the integration (they hold no
 * refresh token of their own) and reports how many. Requires the code
 * `requestPartnerRemovalCode` emailed to the owners.
 */
export async function deletePartnerIntegration(code: string): Promise<RemovePartnerIntegrationResult> {
  const data = await apiFetch<unknown>('/partner/integration', {
    method: 'DELETE',
    body: JSON.stringify({ code }),
  });
  return removePartnerIntegrationResultSchema.parse(data);
}

/**
 * `Api.PartnerIntegration.NotRegistered` — the one 404 that means "empty", not
 * "broken". Matched on the catalog code, never on the message or the bare
 * status: a different 404 on this path (a routing mistake, say) must still
 * surface as an error rather than silently rendering the empty state.
 */
function isNotRegistered(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { errorCode?: unknown }).errorCode === '150204'
  );
}

// ── Access request (the step BEFORE registering a callback) ──────────────────

/**
 * Whether the org has already asked for partner access, and when.
 *
 * Deliberately NOT modelled as "404 means empty" like `getPartnerIntegration`
 * above: never-asked is the common case here, not an edge, so the server
 * answers `{ submitted: false }` rather than making every fresh org's settings
 * page swallow a 404.
 */
export const partnerAccessRequestStatusSchema = z.object({
  submitted: z.boolean(),
  submittedAt: z.string().nullable(),
});

export type PartnerAccessRequestStatus = z.infer<typeof partnerAccessRequestStatusSchema>;

export type SubmitPartnerAccessRequestInput = {
  /**
   * Sent for completeness, but the server IGNORES it and stamps the caller's
   * profile email instead — the field is read-only in the form, and a lock the
   * server does not re-derive is decorative.
   */
  emailAddress: string;
  /** Dial code, `+`-prefixed, e.g. "+1". */
  countryCode: string;
  /** National digits only — no dial code, spaces, or punctuation. */
  phoneNumber: string;
  reason: string;
  inboxVolume: PartnerInboxVolume;
  useCase: PartnerUseCase;
};

export async function getPartnerAccessRequest(): Promise<PartnerAccessRequestStatus> {
  const data = await apiFetch<unknown>('/partner/integration/access-request');
  return partnerAccessRequestStatusSchema.parse(data);
}

/** Files the request. `409 150301` if this org already has one on file. */
export async function submitPartnerAccessRequest(
  input: SubmitPartnerAccessRequestInput,
): Promise<PartnerAccessRequestStatus> {
  const data = await apiFetch<unknown>('/partner/integration/access-request', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return partnerAccessRequestStatusSchema.parse(data);
}

export type NotifyPartnerAccessRequestInput = {
  /** ISO-8601 start of the booked call; `null` when no booking happened. */
  calendlySlotTime: string | null;
};

/** Forwards the org's filed request to our team once the booking step resolves.
 * `204`; `404 150302` if none is on file. The server sends exactly once per org,
 * so repeat calls are no-ops. */
export async function notifyPartnerAccessRequest(
  input: NotifyPartnerAccessRequestInput,
): Promise<void> {
  await apiFetch<void>('/partner/integration/access-request/notify', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Available times for the partner onboarding-call Calendly event (not the onboarding
 * demo's `getAvailableSlots`). Same wire shape, so it reuses `availableSlotsSchema`. */
export async function getPartnerAvailableSlots(startTime?: string): Promise<AvailableSlot[]> {
  const path = startTime
    ? `/partner/integration/access-request/available-slots?startTime=${encodeURIComponent(startTime)}`
    : '/partner/integration/access-request/available-slots';
  const data = await apiFetch<unknown>(path);
  return availableSlotsSchema.parse(data).slots;
}

export const partnerCalendlyBookedResponseSchema = z.object({
  eventUri: z.string(),
});

export type PartnerCalendlyBookedResponse = z.infer<typeof partnerCalendlyBookedResponseSchema>;

export type PartnerCalendlyBookingInput = {
  /** ISO-8601 start time of the slot the user selected. */
  startTime: string;
  timezone?: string;
};

/** Books the mandatory partner onboarding call for this org's filed request. A real
 * booking: Calendly creates the event and emails its own confirmation. */
export async function bookPartnerCalendlySlot(
  input: PartnerCalendlyBookingInput,
): Promise<PartnerCalendlyBookedResponse> {
  const data = await apiFetch<unknown>('/partner/integration/access-request/calendly-booked', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return partnerCalendlyBookedResponseSchema.parse(data);
}
